import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, HStack, Stack, useDisclosure, Text } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { gql, useMutation } from '@apollo/client';
import { isEqual, debounce } from 'lodash';
import { faPlus, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import camelCase from "lodash.camelcase";

import { useProjectsContext } from '../contexts/ProjectsProvider';
import ProjectFormHeader from '../components/ProjectForm/ProjectHeader/ProjectFormHeader';
import useGetProjectTemplate from '../hooks/useGetProjectTemplate';
import useGetProjects from '../hooks/useGetProjects';
import useUpdateForm from '../hooks/useUpdateForm';
import useGetFormStatuses from '../hooks/useGetFormStatus';
import Loader from '../components/Loader';
import { tableFieldsToAutoSave } from '../utils/defaults';
import TemplateSection from '../components/TemplateSection';
import { IFormSection } from '../interfaces/IFormSection';
import FormIterationChanger from '../components/ProjectForm/FormIterationChanger';
import MarkAsCompleteModal from '../components/ProjectForm/MarkAsCompleteModal';
import { getDefaultFieldValue } from '../utils/helpers';
import { hasEmptyRequiredFields } from '../utils/formValidation';
import ConfirmationModal from '../components/ConfirmationModal';
import BinIcon from '../icons/BinIcon';
import Can, { isPermitted } from '../components/Can';
import { useAppContext } from '../contexts/AppProvider';
import { FormErrorBoundary } from '../components/FormErrorBoundary';

const CREATE_FORM_ITERATION = gql`
  mutation ($formId: ID!) {
    createFormIteration(formId: $formId)
  }
`;
const DELETE_FORM_ITERATION = gql`
  mutation ($formIterationInput: FormIterationInput!) {
    deleteFormIteration(formIterationInput: $formIterationInput)
  }
`;

const Project = () => {
  const { user } = useAppContext();
  const params = useParams();
  const _id = params._id as string;
  const page = params.page as string;
  const subsection = params.subsection as string | undefined;
  const formNumber = parseInt(page) - 1;
  const pageNumber = parseInt(subsection || '1');
  const {
    currentProject,
    currentProjectForm,
    setCurrentProjectForm,
    autoSaving,
    setAutoSaving,
    formIterationNumber,
    setFormIterationNumber,
  } = useProjectsContext();
  /** Keep selected form in sync when project refetches (e.g. mark complete / edit) without navigating away */
  useEffect(() => {
    if (!currentProject || currentProject._id !== _id) {
      // No project yet, route changed, or context still holds another project — never keep the previous form.
      setCurrentProjectForm(null);
      return;
    }
    const form = currentProject.forms?.[formNumber];
    if (form) {
      setCurrentProjectForm(form);
    }
  }, [currentProject, formNumber, setCurrentProjectForm, _id]);

  const { loading, refetch: refetchProject, updateCachedFormValues } = useGetProjects({ projectId: _id, withForms: true });
  const { refetch: refetchAllProjects } = useGetProjects({});
  const { projectTemplateLoading } = useGetProjectTemplate({ projectId: _id });
  const { formLoading, refetch } = useGetFormStatuses(_id);
  const [isFieldEmpty, setIsFieldEmpty] = useState<boolean>(true);
  const [isFormNavigating, setIsFormNavigating] = useState<boolean>(false);
  const [isTogglingCompletion, setIsTogglingCompletion] = useState(false);
  const { updateFunction } = useUpdateForm();
  const navigate = useNavigate();
  const scrollRef = useRef<any>();
  const { isOpen: isCompleteModalOpen, onOpen: openCompleteModal, onClose: closeCompleteModal } = useDisclosure();
  const { isOpen: isRunDeleteModalOpen, onOpen: openRunDeleteModal, onClose: closeRunDeleteModal } = useDisclosure();

  const isPermittedToEdit = isPermitted({ user, action: 'projects.edit', data: { project: currentProject } });

  const [createFormIteration] = useMutation(CREATE_FORM_ITERATION);
  const [deleteFormIteration] = useMutation(DELETE_FORM_ITERATION);

  const formIteration = useMemo(() => currentProjectForm?.iterations[formIterationNumber || 0], [currentProjectForm?.iterations, formIterationNumber]);
  const formPages = useMemo(() => (currentProjectForm?.pages && pageNumber >= 1 ? currentProjectForm.pages[pageNumber - 1] : undefined), [currentProjectForm?.pages, pageNumber]);
  /** URL `formNumber` is the 0-based forms[] slot; form.index is the template index (often 1-based). Match by _id. */
  const isCurrentFormSynced =
    !!currentProjectForm?._id &&
    currentProjectForm._id === currentProject?.forms?.[formNumber]?._id;
  const savedValues: any = useRef();
  const lastSavedTableCounts: any = useRef({});
  const justSavedTableIncrease: any = useRef({});
  const formInitializedRef = useRef<string | null>(null);
  /** Max `Object.keys(iteration.values).length` we've successfully reset with; used to ignore transient empty payloads. */
  const lastValuesKeyPeakByIterationRef = useRef<Record<string, number>>({});

  useEffect(() => {
    formInitializedRef.current = null;
    savedValues.current = {};
    lastSavedTableCounts.current = {};
    justSavedTableIncrease.current = {};
    lastValuesKeyPeakByIterationRef.current = {};
  }, [_id]);

  const getPersistedLastSaved = useCallback((fieldName: string): number | undefined => {
    if (!_id || formNumber === undefined || formNumber < 0) return undefined;
    const key = `lastSavedTableCount_${_id}_${formNumber}_${fieldName}`;
    const stored = sessionStorage.getItem(key);
    return stored ? parseInt(stored, 10) : undefined;
  }, [_id, formNumber]);

  const getPersistedArrayData = useCallback((fieldName: string): any[] | undefined => {
    if (!_id || formNumber === undefined || formNumber < 0) return undefined;
    const key = `lastSavedTableData_${_id}_${formNumber}_${fieldName}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return undefined;
    try {
      return JSON.parse(stored);
    } catch {
      return undefined;
    }
  }, [_id, formNumber]);
  
  // Helper to persist lastSavedTableCounts to sessionStorage
  const persistLastSaved = useCallback((fieldName: string, count: number) => {
    if (!_id || formNumber === undefined || formNumber < 0) return;
    const key = `lastSavedTableCount_${_id}_${formNumber}_${fieldName}`;
    sessionStorage.setItem(key, count.toString());
  }, [_id, formNumber]);
  
  // Helper to persist array data to sessionStorage
  const persistArrayData = useCallback((fieldName: string, data: any[]) => {
    if (!_id || formNumber === undefined || formNumber < 0) return;
    const key = `lastSavedTableData_${_id}_${formNumber}_${fieldName}`;
    sessionStorage.setItem(key, JSON.stringify(data));
  }, [_id, formNumber]);
  const previousFormNumberRef = useRef<number>(-1); // Track previous form number to detect form switches
  const formFields = useMemo(() => {
    const formFields: { name: string, type: string, validation: boolean }[] = [];
    if (!currentProjectForm?.pages) {
      return formFields;
    }
    // When `currentProject` is still null (Apollo loading), `currentProject?._id !== _id` is true and
    // would incorrectly yield zero fields — we can still derive fields from `currentProjectForm.pages`.
    if (currentProject != null && currentProject._id !== _id) {
      return formFields;
    }
    currentProjectForm.pages.forEach(page =>
      page.sections.forEach(section =>
        section.rows.forEach(row =>
          row.fields.forEach(field => {
            const formField: { name: string, type: string, validation: boolean } = { name: field.name, type: field.type, validation: false };
            const validations = field?.validations || {};
            if (validations.notEmpty || validations.titleRequired || validations.atLeastOneRowComplete) {
              formField.validation = true;
            }
            formFields.push(formField);
          }),
        ),
      ),
    );
    return formFields;
  }, [currentProject?._id, _id, currentProjectForm?.pages]);

  const fieldDefinitionsByName = useMemo(() => {
    const map: Record<string, { type: string; data?: any[]; hideAddRow?: boolean }> = {}
    if (!currentProjectForm?.pages) return map
    currentProjectForm.pages.forEach((page) =>
      page.sections?.forEach((section) =>
        section.rows?.forEach((row) =>
          row.fields?.forEach((field: { name: string; type: string; data?: any[]; hideAddRow?: boolean }) => {
            map[field.name] = { type: field.type, data: field.data, hideAddRow: field.hideAddRow }
          })
        )
      )
    )
    return map
  }, [currentProjectForm?.pages])

  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
    reset,
    trigger,
    watch,
  } = useForm({
    mode: 'all',
  });

  const emptyFieldCheck = (formData: Record<string, unknown> | undefined) => {
    // Pass all pages so multi-tab forms (e.g. Project Summary) resolve requiredWhen correctly.
    setIsFieldEmpty(hasEmptyRequiredFields(formData, formFields, currentProjectForm?.pages));
  };

  useEffect(() => {
    setFormIterationNumber(0);
    formInitializedRef.current = null;
    return () => {
      setFormIterationNumber(null);
      formInitializedRef.current = null;
      reset({});
      refetchAllProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentFormIterationStatus = currentProject?.forms?.[formNumber]?.iterations[formIterationNumber || 0]?.status;
  useEffect(() => {
    if (formNumber > -1 && currentProject) {
      setIsFormNavigating(true);
      let isCancelled = false;
      previousFormNumberRef.current = formNumber;

      Promise.all([
        refetch(),
        refetchProject()
      ]).then(() => {
        // Do not setCurrentProjectForm here — Apollo updates `currentProject` and the sync effect
        // wires the form. A second set here can race with normalized cache and briefly replace
        // the form with a payload missing `iteration.values` (flash of data then blank).
        setIsFormNavigating(false);
      }).catch(() => {
        if (!isCancelled) {
          setIsFormNavigating(false);
        }
      });

      return () => {
        isCancelled = true;
        setIsFormNavigating(false);
      };
    } else {
      setIsFormNavigating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formNumber,
    formIterationNumber,
    currentProject?._id,
    // currentFormIterationStatus and currentFormIterationsNumber intentionally excluded:
    // they are derived from data returned BY the refetch, so including them causes a
    // feedback loop (refetch → updates currentProject → changes derived values → triggers
    // another refetch → ...) which fires on every tab switch and discards unsaved changes.
  ]);

  useEffect(() => {
    setFormIterationNumber(0);
    if (![3, 6, 9, 10].includes(formNumber)) {
      if (formNumber === 4 && pageNumber === 3) {
        return;
      }
      scrollRef?.current?.scrollTo(0, 0);
    }
  }, [formNumber, pageNumber, setFormIterationNumber]);

  useEffect(() => {
    if (currentProjectForm && isCurrentFormSynced) {
      const rawValues = formIteration?.values;
      const valuesKeyCount =
        rawValues && typeof rawValues === "object" && !Array.isArray(rawValues)
          ? Object.keys(rawValues).length
          : 0;

      const iterationStableKey = `${_id}|${formNumber}|${formIterationNumber ?? 0}|${currentProjectForm._id}`;
      const peak = lastValuesKeyPeakByIterationRef.current[iterationStableKey] ?? -1;
      // Apollo sometimes delivers an empty `values` object momentarily after a fuller payload.
      // Do not reset the RHF state to defaults in that window (causes "shows values then blanks").
      if (valuesKeyCount === 0 && peak > 0) {
        return;
      }

      if (formFields.length === 0 && (currentProjectForm?.pages?.length ?? 0) > 0) {
        return;
      }

      const initKey = `${iterationStableKey}|${valuesKeyCount}`;
      const isAlreadyInitialized = formInitializedRef.current === initKey;

      const newData = formFields.reduce((acc, { name, type }) => {
        if (type === 'projectData') {
          return {
            ...acc,
            [name]: currentProject?.[name] || '',
          };
        }
        const value = formIteration?.values[name];
        let finalValue = value;
        const def = fieldDefinitionsByName[name];
        const templateTableRows = def?.data && Array.isArray(def.data) && def.data.length > 0;
        if (value === undefined) {
          if (
            (type === 'minutesTable' || type === 'minutesObservationTable' || type === 'tableInput') &&
            templateTableRows
          ) {
            finalValue = JSON.parse(JSON.stringify(def.data));
          } else {
            finalValue = getDefaultFieldValue(type);
          }
        } else if (
          type === 'tableInput' &&
          Array.isArray(value) &&
          value.length === 0 &&
          def?.hideAddRow &&
          templateTableRows
        ) {
          /** Fixed-row tables (e.g. Equipment Required): backend may store [] before first save. */
          finalValue = JSON.parse(JSON.stringify(def.data));
        }
        return {
          ...acc,
          [name]: finalValue,
        };
      }, {});
      
      // Table fields initialization
      const tableFields = formFields.filter(f => f.type === 'techSignatureTable' || f.type === 'tableInput');
      if (tableFields.length > 0) {
        tableFields.forEach((f) => {
          const savedValue = formIteration?.values[f.name];
          const savedLen = Array.isArray(savedValue) ? savedValue.length : 0;
          // Always check sessionStorage first - it's the source of truth
          // This ensures we restore the correct value even if ref was reset
          const persistedLastSaved = getPersistedLastSaved(f.name);
          let lastSavedLen = lastSavedTableCounts.current[f.name];
          
          // If we have a persisted value, use it (it's more reliable than ref)
          // Only use ref value if it's higher than persisted (meaning we just saved)
          if (persistedLastSaved !== undefined) {
            if (lastSavedLen === undefined || lastSavedLen < persistedLastSaved) {
              lastSavedLen = persistedLastSaved;
              lastSavedTableCounts.current[f.name] = lastSavedLen;
            }
          } else if (lastSavedLen === undefined) {
            // No persisted value and ref is undefined - will initialize from backend below
            lastSavedLen = undefined;
          }
          
          const hasGuardFlag = justSavedTableIncrease.current[f.name] || false;
          
          // Critical: Only update lastSavedTableCounts when we confirm fresh data
          // Never initialize it from backend data during form init if guard flag is set, as backend might be stale
          if (hasGuardFlag) {
            // Guard flag is set - we have a pending save
            if (lastSavedLen === undefined) {
              // Guard flag is set but lastSaved is undefined - this happens when form init runs
              // before the save completes. Preserve the flag and initialize lastSaved from savedValues
              // (which has the value we're trying to save) rather than from stale backend data
              const savedValueLen = Array.isArray(savedValues.current?.[f.name]) ? savedValues.current[f.name].length : savedLen;
              if (savedValueLen > savedLen) {
                // We have a pending save value higher than backend - use that
                lastSavedTableCounts.current[f.name] = savedValueLen;
              }
              // Keep flag active
              // Keep flag active
            } else if (savedLen < lastSavedLen) {
              // Stale backend data detected - keep guard active, don't update lastSavedTableCounts
              // Keep everything unchanged - guard will prevent overwrite
            } else if (savedLen >= lastSavedLen) {
              // Fresh data confirmed - backend now has our saved data or more
              lastSavedTableCounts.current[f.name] = savedLen;
              persistLastSaved(f.name, savedLen);
              justSavedTableIncrease.current[f.name] = false;
            }
          } else {
            // No guard flag - normal initialization
            // Only initialize lastSavedTableCounts if it's undefined AND we have backend data
            // This handles first-time form load, but preserves it on subsequent inits
            if (lastSavedLen === undefined) {
              lastSavedTableCounts.current[f.name] = savedLen;
              persistLastSaved(f.name, savedLen);
            } else if (savedLen === lastSavedLen) {
              // Backend matches what we have - safe to keep as is
              // Ensure sessionStorage is in sync
              persistLastSaved(f.name, savedLen);
            } else {
              // Backend differs from lastSaved - don't update lastSavedTableCounts
              // This preserves the last saved value even if backend is stale
              // CRITICAL: Persist the preserved value to sessionStorage to prevent it from being overwritten
              persistLastSaved(f.name, lastSavedLen);
            }
          }
          
        });
      }
      
      // Update newData to use saved values when backend is stale
      // This ensures the form displays the correct data even when backend hasn't caught up
      if (tableFields.length > 0) {
        tableFields.forEach(f => {
          const savedValue = formIteration?.values?.[f.name];
          const savedLen = Array.isArray(savedValue) ? savedValue.length : 0;
          const lastSavedLen = lastSavedTableCounts.current[f.name];
          
          // If backend is stale (less than what we saved), use saved value for form display
          if (lastSavedLen !== undefined && savedLen < lastSavedLen) {
            // Try to get saved array data from sessionStorage or savedValues.current
            const persistedData = getPersistedArrayData(f.name);
            const savedArrayData = persistedData || savedValues.current?.[f.name];
            
            if (savedArrayData && Array.isArray(savedArrayData) && savedArrayData.length === lastSavedLen) {
              // Use the saved array data instead of stale backend data
              newData[f.name] = savedArrayData;
            }
          }
        });
      }
      
      // Preserve unsaved changes for non-table fields when switching tabs within the same form.
      // savedValues.current is updated immediately on every change (before the 2s auto-save debounce),
      // so if it has a value for a field that differs from the backend data, the user has typed
      // something that hasn't been persisted yet — we must keep it rather than overwriting with
      // stale backend data.
      const tableFieldNames = new Set(tableFields.map(f => f.name));
      if (savedValues.current && Object.keys(savedValues.current).length > 0) {
        formFields.forEach(({ name, type }) => {
          if (tableFieldNames.has(name) || type === 'projectData') return;
          if (name in savedValues.current && !isEqual(savedValues.current[name], newData[name])) {
            newData[name] = savedValues.current[name];
          }
        });
      }
      
      // Update savedValues.current, but preserve fields with stale backend data
      const updatedSavedValues = JSON.parse(JSON.stringify(newData));
      if (tableFields.length > 0) {
        tableFields.forEach(f => {
          const savedValue = formIteration?.values?.[f.name];
          const savedLen = Array.isArray(savedValue) ? savedValue.length : 0;
          const lastSavedLen = lastSavedTableCounts.current[f.name];
          
          // If we detected stale backend data, keep the old savedValues.current value for this field
          if (lastSavedLen !== undefined && savedLen < lastSavedLen && savedValues.current?.[f.name]) {
            updatedSavedValues[f.name] = savedValues.current[f.name];
          }
        });
      }
      savedValues.current = updatedSavedValues;

      if (!isAlreadyInitialized) {
        formInitializedRef.current = initKey;
        lastValuesKeyPeakByIterationRef.current[iterationStableKey] = Math.max(peak, valuesKeyCount);
        reset(newData);
      }
    } else {
      // Only clear savedValues when we are certain we have switched to a DIFFERENT form.
      // If currentProjectForm is not yet loaded (index === undefined), the refetch is still
      // in-flight after a tab switch — clearing savedValues here would destroy unsaved edits
      // before the preservation logic gets a chance to run.
      const isKnownDifferentForm =
        currentProjectForm !== null &&
        !!currentProjectForm?._id &&
        !isCurrentFormSynced;
      if (isKnownDifferentForm) {
        savedValues.current = {};
      }
      formInitializedRef.current = null;
      reset({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentProjectForm?.index,
    currentProjectForm?._id,
    formNumber,
    formIterationNumber,
    formIteration,
    JSON.stringify(formFields),
    currentProject,
    isCurrentFormSynced,
  ]);

  useEffect(() => {
    // On initial load of the page and template check if section one contains subsections and if it does, redirect to projectId/1/1
    if (
      currentProject?.forms &&
      currentProject.forms[formNumber]?.pages?.length > 1 &&
      !pageNumber
    ) {
      navigate(`/project/${_id}/${formNumber + 1}/1`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  useEffect(() => {
    if (formFields.filter(({ validation }) => validation).length > 0) {
      emptyFieldCheck(watch());
    } else {
      setIsFieldEmpty(false);
    }
    const subscription = watch(async (formData) => {
      if (currentProjectForm) {
        if (formIterationNumber === null || formIterationNumber === undefined) return;
        emptyFieldCheck(formData);
        const newData = formFields.reduce((acc, { name, type }) => {
          if (name in formData) {
            return { ...acc, [name]: formData[name] === undefined ? getDefaultFieldValue(type) : formData[name] };
          }
          return acc;
        }, {});
        
        const photoFields = formFields.filter(f => f.type === 'imageCapture');
        const tableFields = formFields.filter(f => f.type === 'techSignatureTable' || f.type === 'tableInput');
        let fieldsToSaveDirectly = tableFieldsToAutoSave.filter(function (n) { return Object.keys(savedValues.current ?? []).indexOf(n) !== -1; });
        const hasChanges = !isEqual(newData, savedValues.current);
        
        let shouldSkipSave = false;
        // Prevent saving empty arrays that would overwrite non-empty saved photo arrays
        if (photoFields.length > 0 && hasChanges) {
          const wouldOverwritePhotos = photoFields.some(f => {
            const newLen = Array.isArray(newData[f.name]) ? newData[f.name].length : 0;
            const savedLen = Array.isArray(savedValues.current?.[f.name]) ? savedValues.current[f.name].length : 0;
            // Skip save if trying to save empty array when we have saved photos
            return newLen === 0 && savedLen > 0;
          });
          
          if (wouldOverwritePhotos) {
            shouldSkipSave = true;
          }
        }
        
        // Prevent saving empty arrays or decreased counts that would overwrite saved table arrays
        // This prevents refetch from clearing/decreasing technicians/drawings/etc
        // Check even when hasChanges is false, as form init might reset savedValues.current to stale data
        if (tableFields.length > 0 && !shouldSkipSave) {
          const wouldOverwriteTables = tableFields.some(f => {
            const newLen = Array.isArray(newData[f.name]) ? newData[f.name].length : 0;
            const savedLen = Array.isArray(savedValues.current?.[f.name]) ? savedValues.current[f.name].length : 0;
            // Always check sessionStorage first - it's the source of truth
            const persistedLastSaved = getPersistedLastSaved(f.name);
            let lastSavedLen = lastSavedTableCounts.current[f.name];
            
            // If we have a persisted value, use it (it's more reliable than ref)
            // Only use ref value if it's higher than persisted (meaning we just saved)
            if (persistedLastSaved !== undefined) {
              if (lastSavedLen === undefined || lastSavedLen < persistedLastSaved) {
                lastSavedLen = persistedLastSaved;
                lastSavedTableCounts.current[f.name] = lastSavedLen;
              }
            }
            
            const hasGuardFlag = justSavedTableIncrease.current[f.name] || false;
            
            // Skip save if trying to save empty array when we have saved table data
            if (newLen === 0 && savedLen > 0) {
              return true;
            }
            
            // Skip save if count decreased unexpectedly right after we saved an increase
            // This catches refetch resets that happen after autoSave
            if (lastSavedLen !== undefined && newLen < lastSavedLen && hasGuardFlag) {
              return true;
            }
            
            // Skip save if trying to save stale backend data (form matches stale backend count but we saved higher)
            // This catches form reinit with stale data after we just saved an increase
            // Check even when newLen === savedLen because form init might have reset savedValues.current to stale data
            if (lastSavedLen !== undefined && newLen <= savedLen && newLen < lastSavedLen && hasGuardFlag) {
              return true;
            }
            
            // CRITICAL: Also prevent saves when count decreased even without guard flag
            // This catches cases where form init cleared the flag but we still have stale backend data
            if (lastSavedLen !== undefined && newLen < lastSavedLen && !hasGuardFlag) {
              return true;
            }
            
            // If we're about to save an increase and lastSaved is undefined, update it now
            // This ensures lastSavedTableCounts is set even if guard prevents subsequent overwrites
            if (lastSavedLen === undefined && newLen > 0) {
              lastSavedTableCounts.current[f.name] = newLen;
              persistLastSaved(f.name, newLen);
            }
            
            return false;
          });
          
          if (wouldOverwriteTables) {
            shouldSkipSave = true;
          }
        }

        if (
          !shouldSkipSave &&
          currentProjectForm && formIteration &&
          (hasChanges || fieldsToSaveDirectly.length > 0) &&
          (currentProject !== null) &&
          isCurrentFormSynced &&
          formIteration.status !== 'completed' &&
          isPermittedToEdit
        ) {
          setAutoSaving(true);
          savedValues.current = JSON.parse(JSON.stringify(newData)); // Make a copy of an object to remove references
          
          // Track last saved counts for table fields to detect unexpected decreases
          // IMPORTANT: Update lastSavedTableCounts BEFORE setting the guard flag
          // This ensures lastSavedTableCounts always reflects what we're about to save
          if (tableFields.length > 0) {
            tableFields.forEach(f => {
              const len = Array.isArray(savedValues.current[f.name]) ? savedValues.current[f.name].length : 0;
              const arrayData = Array.isArray(savedValues.current[f.name]) ? savedValues.current[f.name] : [];
              const lastSavedLen = lastSavedTableCounts.current[f.name];
              // Update lastSavedTableCounts to the value we're saving
              lastSavedTableCounts.current[f.name] = len;
              // Persist both count and array data to sessionStorage so it survives navigation
              persistLastSaved(f.name, len);
              persistArrayData(f.name, arrayData);
              // Mark if we just saved an increase (to detect refetch resets)
              // Compare against the OLD lastSavedLen (before update) to detect increases
              justSavedTableIncrease.current[f.name] = lastSavedLen !== undefined && len > lastSavedLen;
            });
          }
          
          autoSave();
        }
      }
    })
    return () => {
      subscription.unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, currentProject, formFields, formIteration])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const autoSave = useCallback(
    debounce(async () => {
      // Don't auto-save if the form is already completed
      // Checking both currentFormIterationStatus and currentProjectForm?.iterations[...].status for safety
      const status = currentFormIterationStatus || currentProjectForm?.iterations?.[formIterationNumber || 0]?.status;
      if (status === 'completed') return;

      // set the current date when the signature is signed
      if (formPages) {
        for (const section of formPages.sections) {
          for (const row of section.rows) {
            for (const field of row.fields) {
              if (field.type === 'signatureApproval' && field.saveDateOfSignature && savedValues.current[field.name]) {
                const signatureDate = camelCase(field.name) + 'Date'
                savedValues.current[signatureDate] = new Date()
              } else if (field.type === 'signatureApproval' && field.saveDateOfSignature && !savedValues.current[field.name]) {
                const signatureDate = camelCase(field.name) + 'Date'
                savedValues.current[signatureDate] = null
              }
            }
          }
        }
      }

      await updateFunction({
        variables: {
          values: {
            _id: currentProjectForm?._id,
            projectId: _id,
            status: 'inProgress',
            values: savedValues.current,
            autoSave: true,
            iteration: formIterationNumber,
          },
        },
      });

      // Update Apollo cache so switching tabs shows the saved data (including new signatures)
      if (updateCachedFormValues && currentProjectForm?._id) {
        updateCachedFormValues(_id, currentProjectForm._id, formIterationNumber || 0, savedValues.current);
      }
      
    }, 2000)
    , [currentProject, currentProjectForm?._id, _id, formIterationNumber, getValues, formPages, updateCachedFormValues, currentFormIterationStatus]);

  useEffect(() => {
    return () => {
      autoSave.cancel();
    };
  }, [autoSave]);

  const validateSections = (): boolean => {
    const f = currentProject?.forms;
    if (f === undefined || !f?.length) return false;
    const form = f[formNumber];
    const pages = form?.pages;
    if (!pages?.length) return false;
    const pageIndex = pageNumber - 1;
    if (pageIndex < 0 || pageIndex >= pages.length) return false;
    return !!pages[pageIndex]?.sections;
  };

  const renderLoader = () => (
    <Flex w="full" h="100vh" justify="center" align="center">
      <Loader center />
    </Flex>
  );

  const isFormCompleted = formIteration?.status === 'completed';
  const isLoading = loading || projectTemplateLoading || formLoading || isFormNavigating;
  return (
    <>
      {isLoading ? (
        renderLoader()
      ) : (
        <>
          <MarkAsCompleteModal
            control={control}
            errors={errors}
            trigger={trigger}
            isOpen={isCompleteModalOpen}
            onClose={closeCompleteModal}
            getValues={getValues}
            formIndex={formNumber}
            handleSubmit={handleSubmit}
            iteration={formIterationNumber}
            refetch={refetchProject}
            savedValues={savedValues}
            formIteration={formIteration}
            cancelAutoSave={autoSave.cancel}
          />
          <ConfirmationModal
            modalTitle="Delete test run"
            modalText={`Are you sure you want to delete test run "<b>${currentProjectForm?.name} ${(formIterationNumber || 0) + 1}</b>"?`}
            confirmButtonText="Remove"
            confirmButtonIcon={<BinIcon />}
            cancelButtonText="Discard"
            isOpen={isRunDeleteModalOpen}
            onClose={closeRunDeleteModal}
            confirmAction={async () => {
              const res = await deleteFormIteration({
                variables: {
                  formIterationInput: {
                    formId: currentProjectForm?._id,
                    iteration: formIterationNumber,
                  },
                },
              });
              if (res.data?.deleteFormIteration) {
                await refetchProject();
                await refetch();
                setFormIterationNumber((formIterationNumber || 0) > 0 ? (formIterationNumber || 0) - 1 : 0);
              }
            }}
          />
          <Flex w='full' direction='column' textColor='breNavy' overflow='hidden'>
            <ProjectFormHeader completed={formIteration?.status === 'completed'} />
            <Flex
              w="full"
              h="full"
              p="30px"
              pt="0px"
              overflow="hidden"
            >
              <Stack
                rounded="6px"
                w="full"
                p="20px"
                bg="#FFF"
              >
                <Flex justify="space-between" maxW='full' mb='2'>
                  <Stack>
                    {currentProjectForm?.allowMultipleIterations && (
                      <FormIterationChanger
                        name={currentProjectForm?.name || ''}
                        numberOfIterations={(currentProjectForm?.iterations || []).length}
                      />
                    )}
                    {(currentProjectForm?.pages || []).length > 1 && (
                      <HStack>
                        {currentProjectForm?.pages.map(page => (
                          <Button
                            key={page._id}
                            bg={page.index === pageNumber ? 'breBlack' : "breGray"}
                            color={page.index === pageNumber ? 'breWhite' : "breBlack"}
                            fontSize="md"
                            _hover={{}}
                            onClick={() => navigate(`/project/${_id}/${formNumber + 1}/${page.index}`)}
                            maxW="300px"
                          >
                            <Text
                              whiteSpace='nowrap'
                              isTruncated
                              textOverflow='ellipsis'
                            >{page.name}</Text>
                          </Button>
                        ))}
                      </HStack>
                    )}
                  </Stack>
                  <HStack>
                    {currentProjectForm?.allowMultipleIterations && (currentProjectForm?.iterations || []).length > 1 && (
                      <Can
                        action='projects.edit'
                        data={{ project: currentProject }}
                        yes={() =>
                          <Button
                            rightIcon={<BinIcon />}
                            bg="breGray"
                            color="breBlack"
                            fontSize="sm"
                            h="32px"
                            disabled={currentProjectForm?.iterations.length === 100}
                            _hover={{}}
                            _active={{}}
                            onClick={openRunDeleteModal}
                          >Delete test run</Button>
                        }
                      />
                    )}
                    {currentProjectForm?.allowMultipleIterations && (
                      <Can
                        action='projects.edit'
                        data={{ project: currentProject }}
                        yes={() =>
                          <Button
                            rightIcon={<FontAwesomeIcon icon={faPlus} />}
                            mr={2}
                            bg="breGray"
                            color="breBlack"
                            fontSize="sm"
                            h="32px"
                            disabled={currentProjectForm?.iterations.length === 100}
                            _hover={{}}
                            _active={{}}
                            onClick={async () => {
                              const res = await createFormIteration({
                                variables: {
                                  formId: currentProjectForm?._id,
                                }
                              });
                              if (res.data?.createFormIteration) {
                                await refetchProject();
                                await refetch();
                                setFormIterationNumber(currentProjectForm?.iterations?.length || 0);
                              }
                            }}
                          >Add test run</Button>
                        }
                      />
                    )}
                    {formIteration?.values?.completedSignedDate && isFormCompleted && (
                      <Box color="breNavy" pr="20px">
                        <Flex
                          flexDirection="column"
                          fontSize="sm"
                          justify="center"
                          h="full"
                        >
                          <Flex
                            fontWeight="700"
                            lineHeight="12px"
                          >
                            Completed at
                          </Flex>
                          {(() => {
                            const raw = formIteration?.values?.completedSignedDate;
                            if (raw == null || raw === '') return '—';
                            const d = new Date(raw as string | number | Date);
                            if (Number.isNaN(d.getTime())) return '—';
                            return d.toLocaleDateString('en-uk', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour12: true,
                              hour: 'numeric',
                              minute: 'numeric',
                              hourCycle: 'h12',
                            });
                          })()}
                        </Flex>
                      </Box>
                    )}
                    <Can
                      action='projects.edit'
                      data={{ project: currentProject }}
                      yes={() =>
                        <Button
                          rightIcon={<FontAwesomeIcon icon={faCheck} />}
                          bg="brePink"
                          color="white"
                          fontSize="sm"
                          h="32px"
                          _disabled={autoSaving && !isFieldEmpty ?
                            {
                              opacity: 0.15,
                              cursor: 'not-allowed',
                            } : {
                              color: "breWhite",
                              bg: '#131535',
                              opacity: 0.15,
                              cursor: 'not-allowed'
                            }
                          }
                          disabled={autoSaving || (!isFormCompleted && isFieldEmpty)}
                          _hover={{}}
                          _active={{}}
                          onClick={async () => {
                            if (isFormCompleted) {
                              setIsTogglingCompletion(true);
                              try {
                                const values = {
                                  iteration: formIterationNumber,
                                  _id: currentProjectForm?._id,
                                  status: "inProgress",
                                };
                                await updateFunction({ variables: { values } });
                                await refetchProject();
                                await refetch();
                              } finally {
                                setIsTogglingCompletion(false);
                              }
                            } else {
                              openCompleteModal();
                            }
                          }}
                          isLoading={autoSaving || isTogglingCompletion}
                          loadingText={isTogglingCompletion ? "Opening editor…" : "Saving..."}
                        >
                          {isFormCompleted ? "Edit" : "Mark as complete"}
                        </Button>
                      }
                    />
                  </HStack>
                </Flex>
                <Stack
                  id="parent-container"
                  overflow="auto"
                  ref={scrollRef}
                  mt="0px !important"
                >
                  <Box w="full" minH="calc(100vh - 310px)">
                    <FormErrorBoundary>
                      {validateSections()
                        ? (() => {
                            const sections = currentProjectForm?.pages[pageNumber - 1]?.sections ?? [];
                            const visibleSections = sections.filter((section: IFormSection) => {
                              if (!section?.showWhen?.field) return true;
                              try {
                                const currentValue = watch(section.showWhen.field) ?? getValues(section.showWhen.field);
                                return currentValue === section.showWhen.value;
                              } catch {
                                return true;
                              }
                            });
                            return visibleSections.map((section: IFormSection) => (
                              <TemplateSection
                                key={section._id + formIterationNumber}
                                name={section.name}
                                description={section.description}
                                rows={section.rows}
                                formIteration={formIteration}
                                formIterationNumber={formIterationNumber || 0}
                                savedValues={savedValues}
                                control={control}
                                setValue={setValue}
                                canEditForm={
                                  !!currentProject &&
                                  formIteration?.status !== 'completed' &&
                                  isPermitted({
                                    user,
                                    action: 'projects.edit',
                                    data: { project: currentProject },
                                  })
                                }
                              />
                            ));
                          })()
                        : (
                            <Flex align="center" justify="center" minH="200px" color="gray.500">
                              <Text>{currentProjectForm ? 'Loading form…' : 'No form data.'}</Text>
                            </Flex>
                          )}
                    </FormErrorBoundary>
                  </Box>
                </Stack>
              </Stack>
            </Flex>
          </Flex>
        </>
      )}
    </>
  );
};

export default Project;
